export interface ConfiguratorPreviewContext {
    componentId: string;
    customer: string;
    /**
     * Short-lived `x-jscloud-tok` for the cloud-facing component API.
     * Admin-iframe preview embeds this directly. Storefront plugins (Magento,
     * Shopware, Woo) leave it empty / unset and supply {@link tokenEndpoint}
     * instead, so the merchant's secure_key never lands in the HTML.
     */
    token: string;
    /**
     * Optional URL the bundle fetches at boot to obtain a fresh token. Used
     * by storefront plugin embeds: the plugin proxies a Basic-Auth call to
     * `admin.configento.app/api/v1/storefront/components/{id}/preview-token`
     * server-side and exposes the result here. Response shape:
     * `{ token: string, expires_in: number }`. When present, the bundle
     * ignores `token` (which is typically empty in this flow).
     */
    tokenEndpoint?: string;
    cloudBaseUrl: string;
    /**
     * Host for the V3 admin's storefront-asset endpoint
     * (`POST/GET /api/v1/storefront/assets/...`). Decoupled from
     * `cloudBaseUrl` because asset storage is admin's responsibility
     * (Laravel) — not the V3 cloud's (Java backend). Plugins emit this
     * verbatim from their host config; default is `admin.configento.app`.
     */
    assetBaseUrl?: string;
    taxRate: string;
    /** Display currency of the host shop; see WebshopConfig.currency. */
    currency?: string | undefined;
    /** Host exchange rates into {@link currency}; see WebshopConfig.currencyRates. */
    currencyRates?: Record<string, number> | undefined;
    frontendVersion: string;
    /**
     * UI locale the embedding plugin wants the bundle to render in. Free-form
     * string (e.g. `de_DE`, `de-DE`, `de`, `en`) — the bundle's
     * {@link resolveLocale} normalises it. Falls back to `navigator.language`
     * when omitted.
     */
    uiLocale?: string;
    /**
     * Content locale override — which `title[locale]` / `info[locale]` variant
     * the bundle picks from the component data. Free-form (the component can
     * carry any locale code its merchant defined, e.g. `en_GB`, `pt_BR`), so
     * NOT normalised to the 4 supported UI locales. When omitted the bundle
     * falls back to `component.defaultLocale` (legacy behaviour). The admin
     * preview sets this from its "preview locale" toggle so switching the
     * toggle actually re-renders titles in the chosen language.
     */
    contentLocale?: string;
    /**
     * `'admin-preview'` (default) renders the full configurator chrome —
     * BottomBar with the running price, the inline SummaryPanel, the
     * CombinedImage stacked inside the mount element. That's the look the
     * Configento admin user expects in their iframe preview.
     *
     * `'storefront'` strips the chrome: no BottomBar, no SummaryPanel.
     * The host plugin (Magento, Shopware, Woo) takes over those concerns —
     * the shop has its own price-box and its own configuration summary
     * positioned per merchant setting. The bundle stays a value-input
     * grid, nothing else.
     *
     * The mount element gets `data-embed-mode="<value>"` for CSS hooks.
     */
    embedMode?: 'admin-preview' | 'storefront';
    /**
     * CSS selector for the host-page element that should receive the
     * CombinedImage stack via a React Portal. Storefront plugins point
     * this at their gallery placeholder (Magento default
     * `div.gallery-placeholder`) so the configurator-driven image
     * replaces the static product photo. When the selector finds no
     * element the bundle silently skips — never crashes on theme typos.
     * Ignored when `embedMode !== 'storefront'`.
     */
    combinedImageMount?: string;
    /**
     * Ask the visitor which language to use before rendering the
     * configurator. See {@link import('./api/webshopConfig').WebshopConfig}
     * — plain HTML5 embeds set it as `data-configento-language-select`, and
     * the attribute travels here through `promotePreviewFromConfig`.
     */
    languageSelect?: boolean;
    /** Free text above the gate's flags (`data-configento-language-select-title`). */
    languageSelectTitle?: string;
    /**
     * Locale codes the gate offers, in order. Omitted: derived from the
     * Component's own languages.
     */
    languages?: string[];
    /**
     * Attribute values of the shop product the configurator sits on, keyed
     * by attribute code. Feeds `productattribute` characteristics.
     */
    productAttributes?: Record<string, string>;
    /**
     * Map of CMS-block identifier → resolved HTML (already shop-filtered:
     * `{{widget}}`, `{{media}}`, `{{block}}` directives expanded server-side).
     * The bundle substitutes `$content[identifier]` placeholders in every
     * admin-authored HTML field (Characteristic info, Value info, Static
     * type body) at render time, mirroring the legacy storefront's
     * `parseContentBlocks` flow.
     *
     * Empty / missing → no substitution; placeholders render literally
     * (admin-preview mode is fine without blocks since the resolver lives
     * on the host plugin side).
     */
    cmsBlocks?: Record<string, string>;
}

declare global {
    interface Window {
        _configentoPreview?: ConfiguratorPreviewContext;
    }
}

export function readPreviewContext(): ConfiguratorPreviewContext | undefined {
    return window._configentoPreview;
}
